import {
  QAlert,
  QBox,
  QButton,
  QCenter,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QSpinner,
  QStack,
  QText,
  useCurrentUser,
} from '@qualio/ui-components';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCCTemplate } from '../../../../hooks/useCCTemplate';
import { useCCTemplateVersions } from '../../../../hooks/useCCTemplateVersions';
import { useUpgradeCCTemplate } from '../../../../hooks/useUpgradeCCTemplate';
import { DocumentOverviewContext } from '../Context';
import styles from '../DocumentEditor/DocumentEditor.module.css';
import { HTMLViewer } from '../HTMLViewer';

export const CCTemplateUpgradeButton = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const {
    changeControlWithTemplate,
    qualioDocument,
    refetchDocumentOverviewData,
    refetchChangeControl,
  } = useContext(DocumentOverviewContext);
  const isDraft = qualioDocument?.status_id === 'draft';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { upgradeCCTemplate, isLoading: isUpgrading } = useUpgradeCCTemplate({
    onSuccess: () => {
      refetchDocumentOverviewData();
      refetchChangeControl?.();
      setIsModalOpen(false);
    },
  });

  const { ccTemplateVersions } = useCCTemplateVersions(
    currentUser.companyId,
    changeControlWithTemplate?.template_matrix_id,
  );

  const latestEffectiveID = ccTemplateVersions?.latest_effective_version_id;

  const { ccTemplate: latestTemplate, isLoading: isLoadingTemplate } =
    useCCTemplate(latestEffectiveID ?? undefined, isModalOpen && isDraft);

  const latestCCTemplateVersion = useMemo(() => {
    if (!latestEffectiveID) return null;
    return ccTemplateVersions.versions.find((v) => v.id === latestEffectiveID);
  }, [ccTemplateVersions, latestEffectiveID]);

  const hasUpgradeAvailable = useMemo(() => {
    if (!changeControlWithTemplate || !latestEffectiveID) return false;
    return changeControlWithTemplate.type !== latestEffectiveID;
  }, [changeControlWithTemplate, latestEffectiveID]);

  if (!hasUpgradeAvailable || !isDraft) return null;

  const handleApplyUpgrade = async () => {
    await upgradeCCTemplate({
      documentId: qualioDocument.id,
      ccTemplateVersionId: Number(
        ccTemplateVersions?.latest_effective_version_id,
      ),
    });
  };

  return (
    <>
      <QButton variant="link" onClick={() => setIsModalOpen(true)}>
        {t('buttons.newCCVersionAvailable')}
      </QButton>
      {isModalOpen && (
        <QModal
          size="2xl"
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        >
          <QModalHeader>
            <QText>{t('additionalMessages.reviewChangesPending')}</QText>
          </QModalHeader>
          <QModalBody>
            <QStack spacing={4}>
              <QAlert
                status="warning"
                title={t('modals.ccTemplateUpgradeWarning')}
                description={t('modals.ccTemplateUpgradeDescription', {
                  templateType: changeControlWithTemplate?.template_type,
                  currentVersion: changeControlWithTemplate?.template_version,
                  targetVersion: `${latestCCTemplateVersion?.major_version}.${latestCCTemplateVersion?.minor_version}`,
                })}
              />
              {isLoadingTemplate && (
                <QCenter pb={4}>
                  <QSpinner />
                </QCenter>
              )}
              {!isLoadingTemplate && latestTemplate?.sections && (
                <QBox
                  data-cy="cc-template-sections-container"
                  overflowY="auto"
                  maxHeight="400px"
                >
                  <div
                    className={`${styles['document-editor__document__section-content']} document non-editable-mode section-reset`}
                  >
                    <HTMLViewer sections={latestTemplate.sections} />
                  </div>
                </QBox>
              )}
            </QStack>
          </QModalBody>
          <QModalActions>
            <QButton variant="outline" onClick={() => setIsModalOpen(false)}>
              {t('buttons.cancel')}
            </QButton>
            <QButton
              isLoading={isUpgrading}
              isDisabled={
                isUpgrading || !ccTemplateVersions?.latest_effective_version_id
              }
              onClick={handleApplyUpgrade}
            >
              Apply
            </QButton>
          </QModalActions>
        </QModal>
      )}
    </>
  );
};
